import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { MdSearch } from "react-icons/md";

const LARGO_MAXIMO_NOMBRE = 100;

const normalizar = (valor) => String(valor || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

/**
 * Elegir personas para un grupo nuevo (con nombre) o para agregarlas a uno que ya existe.
 * `alConfirmar` devuelve el error a mostrar, o null si salió bien.
 */
export default function ElegirPersonas({ isOpen, onClose, modo, usuarios, excluir = [], alConfirmar }) {
  const [nombre, setNombre] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [elegidos, setElegidos] = useState(() => new Set());
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const fondoFila = useColorModeValue("gray.50", "whiteAlpha.100");
  const textoSecundario = useColorModeValue("gray.500", "gray.400");
  const esGrupoNuevo = modo === "crear";

  useEffect(() => {
    if (!isOpen) return;
    setNombre("");
    setBusqueda("");
    setElegidos(new Set());
    setError("");
  }, [isOpen]);

  const disponibles = useMemo(() => {
    const buscado = normalizar(busqueda);
    return (usuarios || [])
      .filter((usuario) => !excluir.includes(usuario.id))
      .filter((usuario) => !buscado || normalizar(`${usuario.nombre} ${usuario.correo} ${usuario.rol}`).includes(buscado));
  }, [usuarios, excluir, busqueda]);

  const alternar = (id) => {
    setElegidos((previo) => {
      const siguiente = new Set(previo);
      if (siguiente.has(id)) siguiente.delete(id);
      else siguiente.add(id);
      return siguiente;
    });
  };

  const puedeConfirmar = elegidos.size > 0 && (!esGrupoNuevo || nombre.trim()) && !guardando;

  const confirmar = async () => {
    if (!puedeConfirmar) return;
    setGuardando(true);
    setError("");
    const fallo = await alConfirmar({ nombre: nombre.trim(), usuarioIds: [...elegidos] });
    setGuardando(false);
    if (fallo) setError(fallo);
    else onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent mx="12px">
        <ModalHeader>{esGrupoNuevo ? "Nuevo grupo" : "Agregar personas"}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {esGrupoNuevo && (
            <FormControl mb="14px" isRequired>
              <FormLabel fontSize="sm">Nombre del grupo</FormLabel>
              <Input
                value={nombre}
                onChange={(evento) => setNombre(evento.target.value)}
                maxLength={LARGO_MAXIMO_NOMBRE}
                placeholder="Ej.: Equipo comercial"
                autoFocus
              />
            </FormControl>
          )}
          <InputGroup mb="10px">
            <InputLeftElement pointerEvents="none"><MdSearch /></InputLeftElement>
            <Input
              value={busqueda}
              onChange={(evento) => setBusqueda(evento.target.value)}
              placeholder="Buscar por nombre, correo o rol"
              aria-label="Buscar personas"
              autoFocus={!esGrupoNuevo}
            />
          </InputGroup>
          <Text fontSize="xs" color={textoSecundario} mb="6px">
            {elegidos.size ? `${elegidos.size} ${elegidos.size === 1 ? "persona elegida" : "personas elegidas"}` : "Elige a quiénes"}
          </Text>
          {!usuarios ? (
            <Flex justify="center" py="20px"><Spinner color="brand.500" /></Flex>
          ) : !disponibles.length ? (
            <Text fontSize="sm" color={textoSecundario} py="16px" textAlign="center">
              {busqueda ? "Nadie coincide con la búsqueda." : "No hay más personas para agregar."}
            </Text>
          ) : (
            <Flex direction="column" gap="2px">
              {disponibles.map((usuario) => (
                <Checkbox
                  key={usuario.id}
                  isChecked={elegidos.has(usuario.id)}
                  onChange={() => alternar(usuario.id)}
                  colorScheme="brand"
                  px="8px"
                  py="6px"
                  borderRadius="10px"
                  _hover={{ bg: fondoFila }}
                  sx={{ "& > span:last-of-type": { flex: 1, minW: 0 } }}
                >
                  <Flex align="center" gap="10px" minW={0}>
                    <Avatar size="xs" name={usuario.nombre} />
                    <Box minW={0}>
                      <Text fontSize="sm" fontWeight="600" noOfLines={1}>{usuario.nombre}</Text>
                      {usuario.rol && <Text fontSize="xs" color={textoSecundario} noOfLines={1}>{usuario.rol}</Text>}
                    </Box>
                  </Flex>
                </Checkbox>
              ))}
            </Flex>
          )}
          {error && <Text color="red.400" fontSize="sm" mt="10px">{error}</Text>}
        </ModalBody>
        <ModalFooter gap="8px">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button colorScheme="brand" onClick={confirmar} isDisabled={!puedeConfirmar} isLoading={guardando}>
            {esGrupoNuevo ? "Crear grupo" : "Agregar"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
